import { useQueryClient } from "@tanstack/react-query";
import { type RefObject, useEffect, useId, useState } from "react";
import { cn } from "../lib/utils";
import { MAXIMUMS, useEnvoiTenu } from "./bobines-compteur";
import { CLE_BOBINES, useCatalogue } from "./bobines-requetes";
import {
  AJOUT_RESERVE,
  ajouterEnReserve,
  ajoutFait,
  couleursDe,
  dejaSuivie,
  nomComplet,
  refusAjout,
  titreGamme,
} from "./bobines-textes";
import { FOCUS, Pastille } from "./bobines-ui";
import type { GammeVue, ReferenceVue, VueCatalogue } from "./contract";
import { statutDe } from "./queries";

/**
 * « Ajouter une couleur en reserve » (amendement du 28/09/2026) : mettre en reserve une couleur
 * que l'AMS n'a jamais lue, choisie dans le catalogue de Bambu Studio que l'agent du PC envoie.
 * Une gamme, puis une couleur (pastille et nom) : chaque choix monte sa reserve d'une bobine,
 * par `PUT /api/bobines/reserve`, et la ligne de la couleur apparait dans le stock, son groupe
 * ouvert.
 *
 * Le catalogue n'est lu (`GET /api/bobines/catalogue`) qu'a l'ouverture : `Choix` n'est monte
 * qu'a ce moment-la. Une couleur qui a deja sa ligne dans le stock le dit, avec sa reserve ; la
 * choisir monte aussi sa reserve d'une bobine.
 *
 * Le choix reste ouvert apres un ajout, pour en faire plusieurs a la reception d'une commande.
 * Un ajout a la fois : les couleurs s'eteignent pendant l'envoi (`aria-disabled`, le focus reste
 * sur la couleur choisie), et la couleur choisie reste sous le doigt pendant la relecture
 * (`useEnvoiTenu`, comme les compteurs). Le choix suit le bouton qui l'ouvre dans le DOM : le
 * clavier y entre par Tab, Echap et « Fermer » rendent le focus au bouton.
 */

/** Une couleur du catalogue, telle que le choix la montre. */
type CouleurDuChoix = VueCatalogue["gammes"][number]["couleurs"][number];

/** La cle d'une gamme du catalogue dans le choix : sa marque et son nom. */
function cleGamme(gamme: Pick<GammeVue, "marque" | "gamme">): string {
  return JSON.stringify([gamme.marque, gamme.gamme]);
}

/** Le bouton qui ouvre et ferme le choix, en tete de la carte du stock. */
export function BoutonAjoutReserve({
  ouvert,
  basculer,
  idChoix,
  boutonRef,
}: {
  ouvert: boolean;
  basculer: () => void;
  idChoix: string;
  boutonRef: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <button
      aria-controls={ouvert ? idChoix : undefined}
      aria-expanded={ouvert}
      className={cn(
        "inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-input bg-card px-3 text-[13.5px] font-medium hover:bg-muted",
        FOCUS,
      )}
      onClick={basculer}
      ref={boutonRef}
      type="button"
    >
      <span aria-hidden="true">+</span>
      {AJOUT_RESERVE.ouvrir}
    </button>
  );
}

/** Le choix lui-meme, monte a l'ouverture seulement : c'est lui qui lit le catalogue. */
export function ChoixReserve({
  id,
  gammes,
  fermer,
  ajoutee,
}: {
  id: string;
  /** Les gammes que le stock affiche : pour dire d'une couleur qu'elle y est deja. */
  gammes: readonly GammeVue[];
  fermer: () => void;
  /** Une couleur vient d'etre mise en reserve : le stock ouvre son groupe. */
  ajoutee: (couleur: Pick<ReferenceVue, "marque" | "gamme">) => void;
}) {
  const catalogue = useCatalogue();
  const client = useQueryClient();
  const statut = catalogue.isError ? statutDe(catalogue.error) : null;
  // Une session perdue : la vue se relit, et son echec montre l'ecran de connexion.
  useEffect(() => {
    if (statut === 401 || statut === 403) {
      void client.invalidateQueries({ queryKey: CLE_BOBINES });
    }
  }, [statut, client]);

  return (
    <section
      aria-label={AJOUT_RESERVE.ouvrir}
      className="mt-3 flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-3 md:p-4"
      id={id}
      // Echap ferme le choix, ou que soit le focus dedans.
      onKeyDown={(evenement) => {
        if (evenement.key === "Escape") {
          evenement.preventDefault();
          fermer();
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-[13.5px]">{AJOUT_RESERVE.consigne}</p>
        <button
          className={cn(
            "flex-none rounded-md px-2 py-1 text-[13px] font-medium underline underline-offset-[3px] hover:bg-muted",
            FOCUS,
          )}
          onClick={fermer}
          type="button"
        >
          {AJOUT_RESERVE.fermer}
        </button>
      </div>
      {catalogue.isPending ? (
        <p className="text-[13.5px] text-muted-foreground" role="status">
          {AJOUT_RESERVE.lecture}
        </p>
      ) : catalogue.isError ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[13.5px] text-destructive" role="alert">
            {AJOUT_RESERVE.panne}
          </p>
          <button
            className={cn(
              "rounded-md border border-input bg-card px-2.5 py-1 text-[13px] font-medium hover:bg-muted",
              FOCUS,
            )}
            onClick={() => void catalogue.refetch()}
            type="button"
          >
            {AJOUT_RESERVE.reessayer}
          </button>
        </div>
      ) : catalogue.data.gammes.length === 0 ? (
        <p className="text-[13.5px]">{AJOUT_RESERVE.sansCatalogue}</p>
      ) : (
        <Catalogue ajoutee={ajoutee} catalogue={catalogue.data} gammes={gammes} />
      )}
    </section>
  );
}

function Catalogue({
  catalogue,
  gammes,
  ajoutee,
}: {
  catalogue: VueCatalogue;
  gammes: readonly GammeVue[];
  ajoutee: (couleur: Pick<ReferenceVue, "marque" | "gamme">) => void;
}) {
  const idGamme = useId();
  const [choisie, setChoisie] = useState("");
  const envoi = useEnvoiTenu("reserve");
  // Le dernier ajout, dit par la ligne d'etat ; un refus y passe devant.
  const [annonce, setAnnonce] = useState<string | null>(null);
  const gamme = catalogue.gammes.find((g) => cleGamme(g) === choisie);
  // Les couleurs que le stock affiche deja, par cle : leur reserve part de la.
  const affichees = new Map(gammes.flatMap((g) => g.references.map((r) => [r.cle, r] as const)));

  function ajouter(couleur: CouleurDuChoix, bouton: HTMLElement): void {
    if (gamme === undefined) return;
    // La reserve que la page affiche, 0 pour une couleur qui n'a pas encore de ligne : la
    // console n'ecrit que si la base la porte encore (409 sinon, et la vue se relit).
    const avant = affichees.get(couleur.cle)?.reserve ?? 0;
    const reserve = avant + 1;
    if (reserve > MAXIMUMS.reserve) return;
    const nom = nomComplet({ ...gamme, couleur_nom: couleur.couleur_nom });
    const parti = envoi.envoyer({ cle: couleur.cle, valeur: reserve, avant }, bouton, (passe) => {
      if (passe) {
        setAnnonce(ajoutFait(nom, reserve));
        ajoutee(gamme);
      }
    });
    if (parti) setAnnonce(null);
  }

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <label className="text-[13px] font-medium" htmlFor={idGamme}>
          {AJOUT_RESERVE.gamme}
        </label>
        <select
          className={cn(
            "h-10 w-full rounded-lg border border-input bg-background px-3 text-sm @md:w-[330px]",
            FOCUS,
          )}
          id={idGamme}
          onChange={(evenement) => {
            setChoisie(evenement.target.value);
            setAnnonce(null);
          }}
          value={choisie}
        >
          <option value="">{AJOUT_RESERVE.choisirGamme}</option>
          {catalogue.gammes.map((g) => (
            <option key={cleGamme(g)} value={cleGamme(g)}>
              {titreGamme(g)}
            </option>
          ))}
        </select>
      </div>
      {gamme === undefined ? null : (
        <ul
          aria-label={couleursDe(titreGamme(gamme))}
          className="grid grid-cols-1 gap-2 @sm:grid-cols-2 @2xl:grid-cols-3"
        >
          {gamme.couleurs.map((couleur) => (
            <li key={couleur.cle}>
              <CouleurAChoisir
                couleur={couleur}
                enCours={envoi.enVol?.cle === couleur.cle}
                eteinte={envoi.enCours}
                nom={nomComplet({ ...gamme, couleur_nom: couleur.couleur_nom })}
                onChoisir={(bouton) => ajouter(couleur, bouton)}
                suivie={affichees.get(couleur.cle)}
              />
            </li>
          ))}
        </ul>
      )}
      <p
        className={cn("min-h-5 text-[13.5px]", envoi.erreur === null ? "" : "text-destructive")}
        role="status"
      >
        {envoi.erreur === null ? (annonce ?? "") : refusAjout(envoi.erreur)}
      </p>
    </>
  );
}

function CouleurAChoisir({
  couleur,
  nom,
  suivie,
  enCours,
  eteinte,
  onChoisir,
}: {
  couleur: CouleurDuChoix;
  /** Le nom complet : « Noir, PLA Basic ». */
  nom: string;
  /** Sa ligne dans le stock, quand elle en a une. */
  suivie: ReferenceVue | undefined;
  /** C'est elle qui part. */
  enCours: boolean;
  /** Un ajout est en vol : aucune couleur ne part. */
  eteinte: boolean;
  onChoisir: (bouton: HTMLElement) => void;
}) {
  const idNote = useId();
  const pleine = (suivie?.reserve ?? 0) >= MAXIMUMS.reserve;
  const eteint = eteinte || pleine;
  const note = enCours ? "enregistrement..." : dejaSuivie(suivie);
  return (
    <button
      aria-describedby={note === null ? undefined : idNote}
      aria-disabled={eteint || undefined}
      aria-label={ajouterEnReserve(nom)}
      className={cn(
        "flex min-h-11 w-full items-center gap-2.5 rounded-lg border border-input bg-card px-2.5 py-2 text-left",
        eteint ? "cursor-default" : "hover:bg-muted",
        FOCUS,
      )}
      onClick={(evenement) => {
        if (!eteint) onChoisir(evenement.currentTarget);
      }}
      type="button"
    >
      <Pastille hex={couleur.couleur_hex} />
      <span className="flex min-w-0 flex-col">
        <span className="text-[14px] [overflow-wrap:anywhere]">{couleur.couleur_nom}</span>
        {note === null ? null : (
          <span className="text-[12.5px] text-muted-foreground" id={idNote}>
            {note}
          </span>
        )}
      </span>
    </button>
  );
}
